/**
 * 開發環境專用：重現 Notion「分店合約」六種情境（另加即將到期＋點數不足的疊加）。
 * 用法：任一頁網址加 ?contractScenario=expiredEmpty，之後整個分頁都套用；?contractScenario=off 取消。
 * 正式版 NODE_ENV 不是 development，這裡永遠回 null，不會出現在畫面上。
 */
import { ALERT_TYPES, CONTRACT_STATES, SMS_STATES } from "./branchContractFormat";

const STORAGE_KEY = "branchContractDevScenario";
const PARAM = "contractScenario";

const BASE_CONTRACT = {
  id: 1,
  contractName: "OBS黑曜雲診所系統使用合約",
  startDate: "2026-01-01",
  originalEndDate: "2027-01-01",
  bonusMonths: 2,
  bonusDays: 0,
  systemEndDate: "2027-03-01",
  amount: 120000,
  paymentStatus: "PAID",
  paidOn: "2026-01-05",
};

const TIMELINE = { totalDays: 424, contractDays: 365 };

const status = (state, today, daysRemaining, elapsedDays) => ({
  today,
  contractStatus: {
    state,
    expiringFrom: "2027-01-01",
    daysRemaining,
    bonusTotalDays: 59,
    timeline: { ...TIMELINE, elapsedDays },
  },
});

const ACTIVE = status(CONTRACT_STATES.ACTIVE, "2026-10-13", 139, 285);
const EXPIRING = status(CONTRACT_STATES.EXPIRING, "2027-01-15", 45, 379);
const EXPIRED = status(CONTRACT_STATES.EXPIRED, "2027-03-10", 0, 424);

export const DEV_SCENARIOS = Object.freeze({
  active: { label: "1. 合約有效", ...ACTIVE, balance: 850 },
  smsLow: { label: "2. 點數低於 100", ...ACTIVE, balance: 99 },
  expiring: { label: "3. 剩 2 個月內", ...EXPIRING, balance: 850 },
  expiringSmsLow: { label: "3+2. 剩 2 個月內且點數不足", ...EXPIRING, balance: 40 },
  expiredWithPoints: { label: "4. 已到期仍有點數", ...EXPIRED, balance: 320 },
  expiredEmpty: { label: "5. 已到期且點數為 0", ...EXPIRED, balance: 0 },
  unpaid: { label: "6. 款項未收", ...ACTIVE, balance: 850, contract: { paymentStatus: "UNPAID", paidOn: null } },
  partial: {
    label: "7. 部分收款",
    ...ACTIVE,
    balance: 850,
    contract: { paymentStatus: "PARTIAL", paidOn: null, paidAmount: 80000, unpaidAmount: 40000 },
  },
});

function isDevelopment() {
  return process.env.NODE_ENV === "development";
}

export function getDevScenarioKey() {
  if (!isDevelopment() || typeof window === "undefined") return null;
  try {
    const fromUrl = new URLSearchParams(window.location.search).get(PARAM);
    if (fromUrl === "off") {
      window.sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    if (fromUrl && DEV_SCENARIOS[fromUrl]) {
      window.sessionStorage.setItem(STORAGE_KEY, fromUrl);
      return fromUrl;
    }
    const stored = window.sessionStorage.getItem(STORAGE_KEY);
    return stored && DEV_SCENARIOS[stored] ? stored : null;
  } catch (e) {
    return null;
  }
}

function smsState(balance) {
  if (balance <= 0) return SMS_STATES.EMPTY;
  if (balance < 100) return SMS_STATES.LOW;
  return SMS_STATES.OK;
}

function buildAlerts(scenario) {
  const alerts = [];
  if (scenario.contractStatus.state === CONTRACT_STATES.EXPIRED) {
    alerts.push({ type: ALERT_TYPES.CONTRACT_EXPIRED });
  } else if (scenario.contractStatus.state === CONTRACT_STATES.EXPIRING) {
    alerts.push({ type: ALERT_TYPES.CONTRACT_EXPIRING, daysRemaining: scenario.contractStatus.daysRemaining });
  }
  if (scenario.balance < 100) alerts.push({ type: ALERT_TYPES.SMS_LOW, balance: scenario.balance });
  return alerts;
}

const PLANS = [
  { code: "PLAN_1", name: "方案一", amount: 1000, points: 1100, bonusPoints: 100 },
  { code: "PLAN_2", name: "方案二", amount: 2000, points: 2200, bonusPoints: 200 },
  { code: "PLAN_3", name: "方案三", amount: 3000, points: 3300, bonusPoints: 300 },
];

const DEV_PAYMENT_INFO = {
  bank: { bankCode: "000", bankName: "（開發假資料）", accountNumber: "0000-0000-0000", accountName: "（開發假資料）" },
  lineUrl: null,
};

export function buildDevDetail(key, branchId, branchName) {
  const scenario = DEV_SCENARIOS[key];
  const canTopup = scenario.contractStatus.state !== CONTRACT_STATES.EXPIRED;
  return {
    today: scenario.today,
    branchId,
    branchName,
    contract: { ...BASE_CONTRACT, ...(scenario.contract || {}) },
    contractStatus: scenario.contractStatus,
    sms: { state: smsState(scenario.balance), balance: scenario.balance, sentCount: 4280 },
    canSendSms: scenario.balance > 0,
    canTopup,
    alerts: buildAlerts(scenario),
    lowBalanceThreshold: 100,
    plans: PLANS,
    paymentInfo: DEV_PAYMENT_INFO,
  };
}

export function buildDevSummary(key, branches) {
  const scenario = DEV_SCENARIOS[key];
  return {
    today: scenario.today,
    branches: branches.map((branch) => ({
      branchId: branch.id,
      branchName: branch.branch_name,
      systemEndDate: BASE_CONTRACT.systemEndDate,
      contractState: scenario.contractStatus.state,
      daysRemaining: scenario.contractStatus.daysRemaining,
      smsState: smsState(scenario.balance),
      smsBalance: scenario.balance,
      alerts: buildAlerts(scenario),
    })),
  };
}

const SMS_FEE_ROWS = [
  { itemType: "SMS_TOPUP", itemLabel: "簡訊加值", sourceId: 2, feeDate: "2026-08-10", description: "方案一：加值 1,100 點", amount: 1000, paymentStatus: "PAID", paymentLabel: "已收款" },
  { itemType: "SMS_TOPUP", itemLabel: "簡訊加值", sourceId: 1, feeDate: "2026-05-18", description: "方案二：加值 2,200 點", amount: 2000, paymentStatus: "PAID", paymentLabel: "已收款" },
];

export function buildDevFees(key) {
  const paymentStatus = DEV_SCENARIOS[key].contract?.paymentStatus;
  if (paymentStatus === "PARTIAL") {
    return [
      { itemType: "CONTRACT", itemLabel: "主約款項（第三期）", sourceId: 3, feeDate: null, description: "OBS黑曜雲診所系統使用合約－第三期收款", amount: 40000, paymentStatus: "UNPAID", paymentLabel: "未收款" },
      ...SMS_FEE_ROWS,
      { itemType: "CONTRACT", itemLabel: "主約款項（第二期）", sourceId: 2, feeDate: "2026-04-15", description: "OBS黑曜雲診所系統使用合約－第二期收款", amount: 40000, paymentStatus: "PAID", paymentLabel: "已收款" },
      { itemType: "CONTRACT", itemLabel: "主約款項（第一期）", sourceId: 1, feeDate: "2026-01-05", description: "OBS黑曜雲診所系統使用合約（首期年約含贈送2個月）－第一期收款", amount: 40000, paymentStatus: "PAID", paymentLabel: "已收款" },
    ];
  }
  const contractRow = {
    itemType: "CONTRACT",
    itemLabel: "主約款項",
    sourceId: 1,
    feeDate: "2026-01-05",
    description: "OBS黑曜雲診所系統使用合約（含贈送2個月）",
    amount: 120000,
    paymentStatus: paymentStatus === "UNPAID" ? "UNPAID" : "PAID",
    paymentLabel: paymentStatus === "UNPAID" ? "未收款" : "已收款",
  };
  if (paymentStatus === "UNPAID") return [contractRow, ...SMS_FEE_ROWS];
  return [...SMS_FEE_ROWS, contractRow];
}

export function buildDevSmsLogs({ page = 1, pageSize = 20 } = {}) {
  const total = 45;
  const items = [];
  for (let i = (page - 1) * pageSize; i < Math.min(page * pageSize, total); i += 1) {
    const failed = i % 9 === 4;
    items.push({
      id: total - i,
      sentAt: `2026-10-${String(13 - Math.floor(i / 5)).padStart(2, "0")} ${String(18 - (i % 5)).padStart(2, "0")}:05:00`,
      phone: `09${String(12 + (i % 80)).padStart(2, "0")}***${String(100 + i).slice(-3)}`,
      triggerLabel: i % 3 === 0 ? "群發簡訊" : "預約通知",
      content: "【OBS 中山診所】提醒您明天 10:00 回診，如需更改請來電。",
      status: failed ? "FAILED" : "SUCCESS",
      statusLabel: failed ? "失敗" : "成功",
      pointsCharged: failed ? 0 : 1,
    });
  }
  return { items, page, pageSize, total, phoneMasked: true };
}
